import { Controller, type FieldValues } from 'react-hook-form';
import { Input, type InputProps } from 'antd';
import isNil from 'lodash/isNil';

import {
  extractPosFormItemProps,
  type PosFormComponentType,
} from './FormUtils.ts';
import { PosFormItem } from './PosFormItem.tsx';

export type PosInputProps<TFieldValues extends FieldValues = FieldValues> =
  PosFormComponentType<InputProps, TFieldValues>;

/**
 * @component
 * @description Text input with React-Hook-Form integration
 *
 * @param PosInputProps.control Control field from React-Hook-Form useForm
 * @param PosInputProps.name Name of form field
 * @example
 * ```tsx
 * // Controlled (RHF) usage:
 * const { t } = useTranslation();
 * const { control } = useForm<CustomerForm>();
 *
 * <PosInput
 *   control={control}
 *   name="FirstName"
 *   label={t('common.people.firstName')}
 * />
 *
 * // Uncontrolled (No RHF) usage:
 * const [enteredText, setEnteredText] = useState<string>();
 *
 * <PosInput
 *   onChange={setEnteredText}
 *   value={enteredText}
 * />
 * ```
 * @remarks If an invalid control from useForm is passed then the Input will behave as an uncontrolled component and field name will not be type-safe
 * @see {@link https://ant.design/components/input | AntD Input Component}
 */
export const PosInput = <TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  children,
  ...props
}: PosInputProps<TFieldValues>) => {
  const isControlled = !!control;
  const ariaLabel = typeof props.label === 'string' ? props.label : name;

  const { posFormItemProps, rest: inputProps } = extractPosFormItemProps(props);
  const onControlledChange = (
    value: string | null | undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (...event: any[]) => void
  ) => {
    const controlledValue = isNil(value) ? '' : value;
    onChange(controlledValue);
  };

  if (isControlled)
    return (
      <PosFormItem control={control} name={name} {...posFormItemProps}>
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, onBlur, ...field } }) => (
            <Input
              {...inputProps}
              {...field}
              onChange={(e) => onControlledChange(e.target.value, onChange)}
              onBlur={(e) => {
                onBlur();
                inputProps.onBlur?.(e);
              }}
              data-testid={name}
              aria-label={ariaLabel}
            />
          )}
        />
      </PosFormItem>
    );

  return <Input name={name} {...props} />;
};
